import type { Reconciliation, VarianceRow } from '@orgus/protocol';
import { formatUsd } from '@orgus/protocol';
import { CircleAlert, Info, Scale } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { DataTable, EmptyState, Panel, RowStatus, Tooltip, cn, formatCompact, formatMoneyExact, formatPercent, plural } from '@orgus/ui';
import type { Column, RowStatusProps } from '@orgus/ui';
import { niceCeil } from '../costs/DailySpendChart.tsx';
import { dayLabel } from '../costs/model.ts';
import { dayRangeText, formatVariancePct, varianceBuckets, varianceStatus } from './model.ts';
import { Dash, ReportGate, ReportSkeleton, ScopeBar, useScope } from './Scope.tsx';
import { StatStrip } from '../fleet/StatStrip.tsx';
import { SyncRow } from './SyncRow.tsx';
import { useFinance, useFinanceReport } from './useFinance.ts';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';

/** The row's one status, plain (status.md): over threshold needs a look; within is quiet. */
function rowStatus(r: VarianceRow): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const s = varianceStatus(r);
  return s.tone === 'waiting'
    ? { tone: 'attention', icon: 'warning', label: s.label }
    : s.tone === 'passed'
      ? { tone: 'neutral', icon: 'done', label: s.label }
      : { tone: 'neutral', icon: 'queued', label: s.label };
}

const noVariance = (r: VarianceRow) => r.missing || r.unmapped || (r.billing === 0 && r.telemetry === 0);

/**
 * Orgus, the bill and the variance per day, model or key. On a phone each row is two lines:
 * the label (its status as an icon before it, wrapping rather than cut) over an "Orgus $x"
 * caption, with Variance on the right; Orgus, Billed and Status return as columns from md up.
 * Status is left out on a phone rather than hidden, so Variance is the last column and keeps
 * the table's right gutter.
 */
function columns(first: string, kind: 'day' | 'model' | 'key', narrow: boolean): Column<VarianceRow>[] {
  const orgus = (r: VarianceRow) => (r.unmapped ? <Dash label="Unknown" /> : formatUsd(r.telemetry));
  return [
    {
      key: 'key',
      header: first,
      grow: true,
      text: (r) => `${r.label} ${r.key}`,
      sortValue: (r) => r.key,
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <RowStatus {...rowStatus(r)} hideLabel className="md:hidden" />
          <span className="flex min-w-0 flex-col">
            <span className={cn('text-fg', narrow ? 'whitespace-normal break-words' : 'truncate whitespace-nowrap')}>{kind === 'day' ? dayLabel(r.key) : r.label}</span>
            {narrow ? (
              <span className="text-caption text-fg-subtle num">Orgus {orgus(r)}</span>
            ) : (
              kind === 'key' && r.key && <span className="truncate font-mono text-caption text-fg-subtle">{r.key}</span>
            )}
          </span>
        </span>
      ),
    },
    ...(narrow ? [] : [{ key: 'telemetry', header: 'Orgus', type: 'number' as const, sortValue: (r: VarianceRow) => r.telemetry, cell: orgus }]),
    {
      key: 'billing',
      header: kind === 'key' ? 'Billed tokens at list' : 'Billed',
      type: 'number',
      hideBelow: 'md',
      sortValue: (r) => r.billing,
      cell: (r) => (r.missing ? <Dash label="Not billed yet" /> : formatUsd(r.billing)),
    },
    ...(kind === 'key'
      ? [{ key: 'tokens', header: 'Tokens', type: 'number' as const, hideBelow: 'lg' as const, sortValue: (r: VarianceRow) => r.billingTokens ?? 0, cell: (r: VarianceRow) => formatCompact(r.billingTokens ?? 0) }]
      : []),
    {
      key: 'variance',
      header: 'Variance',
      type: 'number',
      sortValue: (r) => (r.missing || r.unmapped ? null : Math.abs(r.variancePct ?? 0)),
      cell: (r) =>
        noVariance(r) ? (
          <Dash label="No variance" />
        ) : (
          <span className={cn('whitespace-nowrap num', r.flagged ? 'font-medium text-fg' : 'text-fg-muted')}>
            {formatMoneyExact(r.variance)}
            <span className="hidden text-fg-subtle md:inline"> ({formatVariancePct(r.variancePct)})</span>
          </span>
        ),
    },
    ...(narrow ? [] : [{ key: 'status', header: 'Status', text: (r: VarianceRow) => varianceStatus(r).label, cell: (r: VarianceRow) => <RowStatus {...rowStatus(r)} /> }]),
  ];
}

/** The plot's width, followed as the panel resizes. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([e]) => setWidth(e?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** The narrowest a paired bar may get before the chart folds days into weeks. */
const MIN_BAR_PAIR = 8;

/**
 * Orgus against the bill, day by day: paired bars (Orgus, then the bill) and a mark under
 * days over the threshold. Too narrow for a bar a day (a phone), it draws a bar a week. A
 * table carries the same numbers.
 */
function VarianceChart({ days }: { days: VarianceRow[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const [plotRef, width] = useWidth<HTMLDivElement>();
  const size = width > 0 && width / Math.max(1, days.length) < MIN_BAR_PAIR ? 7 : 1;
  const bars = varianceBuckets(days, size);
  const max = niceCeil(Math.max(0, ...bars.flatMap((d) => [d.telemetry, d.billing])));
  const shown = bars[hover ?? bars.length - 1];
  return (
    <figure className="flex flex-col gap-3">
      <figcaption className="flex flex-wrap items-baseline justify-between gap-3 text-ui">
        <span className="text-fg-muted">
          {shown ? (
            <>
              <span className="text-fg">{dayRangeText(shown.from, shown.to)}</span>: Orgus <span className="font-medium text-fg num">{formatUsd(shown.telemetry)}</span>, billed{' '}
              <span className="font-medium text-fg num">{shown.missing ? 'not yet' : formatUsd(shown.billing)}</span>
              {/* A run with unbilled days names the days the bill covers; the variance shows only when both cover the same days. */}
              {!shown.missing && !shown.complete && shown.billedFrom && shown.billedTo && <span className="text-fg-subtle"> for {dayRangeText(shown.billedFrom, shown.billedTo)} only</span>}
              {!shown.missing && shown.complete && <span className="text-fg-subtle num">, {formatVariancePct(shown.variancePct)}</span>}
            </>
          ) : (
            'No days'
          )}
        </span>
        <span className="flex items-center gap-3 text-caption text-fg-subtle">
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="size-2 rounded-sm" style={{ background: 'var(--hue-1)' }} />
            Orgus
          </span>
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="size-2 rounded-sm" style={{ background: 'var(--hue-3)' }} />
            Billed
          </span>
          <span className="flex items-center gap-1.5">
            <CircleAlert className="size-3 text-waiting" aria-hidden />
            Over threshold
          </span>
        </span>
      </figcaption>
      <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2">
        <div className="flex h-40 flex-col justify-between text-right text-caption text-fg-subtle num" aria-hidden>
          {[max, max / 2, 0].map((t) => (
            <span key={t} className="-translate-y-1/2 first:translate-y-0 last:translate-y-0">
              {formatUsd(t)}
            </span>
          ))}
        </div>
        <div ref={plotRef} className="relative h-40" onMouseLeave={() => setHover(null)}>
          <div aria-hidden className="pointer-events-none absolute inset-0 flex flex-col justify-between">
            {[0, 1, 2].map((t) => (
              <span key={t} className="border-t border-border-subtle" />
            ))}
          </div>
          <div className="absolute inset-0 flex items-end gap-0.5" role="presentation">
            {bars.map((d, i) => (
              <button key={d.key} type="button" tabIndex={-1} aria-hidden onMouseEnter={() => setHover(i)} className="relative flex h-full min-w-0 flex-1 items-end gap-px">
                {hover === i && <span aria-hidden className="absolute inset-0 -z-10 rounded-sm bg-hover" />}
                <span className="flex-1 rounded-t-[2px]" style={{ height: `${max > 0 ? (d.telemetry / max) * 100 : 0}%`, background: 'var(--hue-1)' }} />
                <span className={cn('flex-1 rounded-t-[2px]', d.missing && 'opacity-0')} style={{ height: `${max > 0 ? (d.billing / max) * 100 : 0}%`, background: 'var(--hue-3)' }} />
              </button>
            ))}
          </div>
        </div>
        <span />
        <div className="mt-1 flex flex-col gap-1">
          <div className="flex gap-0.5" aria-hidden>
            {bars.map((d) => (
              <span key={d.key} className="flex min-w-0 flex-1 justify-center">
                {d.flagged ? <CircleAlert className="size-3 text-waiting" /> : null}
              </span>
            ))}
          </div>
          <div className="flex justify-between text-caption text-fg-subtle" aria-hidden>
            <span>{days[0] ? dayLabel(days[0].key) : ''}</span>
            <span>{days.length ? dayLabel(days[days.length - 1]!.key) : ''}</span>
          </div>
        </div>
      </div>
      <table className="sr-only">
        <caption>Orgus spend against the bill per day</caption>
        <thead>
          <tr>
            <th>Day</th>
            <th>Orgus</th>
            <th>Billed</th>
            <th>Variance</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.key}>
              <td>{dayLabel(d.key)}</td>
              <td>{formatUsd(d.telemetry)}</td>
              <td>{d.missing ? 'Not billed yet' : formatUsd(d.billing)}</td>
              <td>{formatVariancePct(d.missing ? null : d.variancePct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

function Body({ rec, billingMode }: { rec: Reconciliation; billingMode: 'subscription' | 'api' }) {
  const finance = useFinance();
  const narrow = useMediaQuery(NARROW_QUERY);
  const configured = finance?.sync.billing.configured ?? false;
  if (rec.status !== 'ready') {
    return (
      <Panel>
        <EmptyState
          size="page"
          icon={<Scale />}
          title={rec.status === 'subscription' ? 'Subscription seats have no per-token bill' : configured ? 'No billing data for this period yet' : 'Connect the bill to reconcile'}
          description={
            rec.status === 'subscription'
              ? 'Spend is notional while Claude Code runs on Pro or Max seats, so there is nothing to reconcile. If some work runs on an API key, add an Admin API key and Orgus reconciles that part.'
              : configured
                ? 'The provider report lags by a few minutes to a day, and the first pull covers the last 92 days. Pull now, or pick a period that has ended.'
                : 'Orgus pulls your organization usage and cost report every night and compares it with what transcripts recorded, by day, model and key. It needs an Admin API key (sk-ant-admin01-...).'
          }
          action={
            !configured ? (
              <Link to="/settings/finance" className="text-sm font-medium text-fg underline-offset-2 hover:underline">
                Add an Admin API key
              </Link>
            ) : undefined
          }
        />
      </Panel>
    );
  }
  const flaggedDays = rec.byDay.filter((d) => d.flagged).length;
  const over = rec.variancePct !== null && Math.abs(rec.variancePct) > rec.threshold;
  return (
    <div className="flex flex-col gap-5">
      <StatStrip
        label="Orgus against the bill"
        stats={[
          { label: 'Orgus recorded', value: formatUsd(rec.telemetry), sub: billingMode === 'api' ? 'Tokens at list price' : 'Notional, at list price' },
          { label: 'Billed tokens', value: formatUsd(rec.billing), sub: `Over ${plural(rec.byDay.length - rec.missingDays.length, 'day')}` },
          {
            label: 'Variance',
            tone: over ? 'attention' : undefined,
            value: formatVariancePct(rec.variancePct),
            sub: `${over ? 'Over' : 'Within'} ${formatPercent(rec.threshold)}, ${formatUsd(rec.variance)}`,
          },
          {
            label: 'Other charges',
            value: formatUsd(rec.billingOther),
            sub: (
              <Tooltip content="On the bill besides tokens: web search, code execution and managed sessions">
                <span tabIndex={0} className="rounded-control">
                  Not tokens
                </span>
              </Tooltip>
            ),
          },
        ]}
      />
      <Panel
        title="Orgus against the bill"
        sub={flaggedDays ? `${plural(flaggedDays, 'day')} over${narrow ? '' : ' threshold'}` : narrow ? 'All within' : 'Every billed day within threshold'}
        icon={<Scale />}
      >
        <VarianceChart days={rec.byDay} />
      </Panel>
      <div className="flex flex-col gap-5">
        <section aria-label="By model" className="flex flex-col gap-2">
          <h2 className="text-base font-medium text-fg">By model</h2>
          <DataTable label="Variance by model" rows={rec.byModel} columns={columns('Model', 'model', narrow)} rowKey={(r) => r.key} pageSize={false} initialSort={{ key: 'billing', dir: 'desc' }} />
        </section>
        <section aria-label="By key" className="flex flex-col gap-2">
          <h2 className="flex min-w-0 items-baseline gap-2 text-base font-medium text-fg">
            By API key
            <Link to="/settings/finance" className="hidden truncate text-caption font-normal text-fg-subtle underline-offset-2 hover:text-fg-muted hover:underline md:inline">
              Map keys to machines
            </Link>
          </h2>
          <DataTable label="Variance by API key" rows={rec.byKey} columns={columns('Key', 'key', narrow)} rowKey={(r) => r.key || 'none'} pageSize={false} initialSort={{ key: 'billing', dir: 'desc' }} />
        </section>
      </div>
      <section aria-label="By day" className="flex flex-col gap-2">
        <h2 className="text-base font-medium text-fg">By day</h2>
        <DataTable label="Variance by day" rows={[...rec.byDay].reverse()} columns={columns('Day', 'day', narrow)} rowKey={(r) => r.key} pageSize={14} />
      </section>
      <p className="flex items-center gap-2 text-caption text-fg-subtle">
        <Info className="size-3.5 shrink-0" aria-hidden />
        <span className="min-w-0 truncate">The bill counts UTC days</span>
        <Tooltip content="Orgus counts this hub's local days, so a late session can land on the neighbouring day; weekly and period totals are not affected. The cost report has no key dimension, so a key's billed amount is its billed tokens at list price.">
          <span tabIndex={0} className="shrink-0 rounded-control underline decoration-dotted underline-offset-2">
            Why days can differ
          </span>
        </Tooltip>
      </p>
    </div>
  );
}

/** Telemetry against billing truth, by day, model and key, with the variance highlighted. */
export function ReconcileView() {
  const scope = useScope();
  const result = useFinanceReport({ ...scope, projectId: null, account: null });
  const finance = useFinance();
  return (
    <>
      <ScopeBar scope={scope} hideProject hideAccount result={result} note="Whole organization" />
      {finance && (
        <div className="mb-5">
          <SyncRow status={finance.sync.billing} />
        </div>
      )}
      <ReportGate result={result} skeleton={<ReportSkeleton tiles={4} />}>
        {(r) => <Body rec={r.reconciliation} billingMode={r.billing} />}
      </ReportGate>
    </>
  );
}
